import { useCallback, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Mention, MentionsInput } from 'react-mentions'

import { debounce } from 'helpers/hooks'
import { getSearchedCreators } from 'services/endpoints/global'
import { type IProfile } from 'types/interfaces/IProfile'
import { type ITag } from 'types/interfaces/ITypes'

interface IEntry {
    id: string | number
    display: string
    name: string
    avatarUrl: string
    friends: boolean
    subscribed: boolean
}

interface PostTextProps {
    body: string
    tags: ITag[]
    setText: (body: string, tags: ITag[]) => void
}

export const PostText = ({ body, setText }: PostTextProps) => {
    const [localMentions, setLocalMentions] = useState<IEntry[]>([])

    const { t } = useTranslation()

    const textWrapRef = useRef<HTMLDivElement>(null)

    const focusTextarea = () => {
        const textarea = textWrapRef.current?.querySelector('textarea')
        textarea?.focus()
    }

    const getCreators = async (term: string, callback: any) => {
        const { data } = await getSearchedCreators(term)

        const transformedResponse = data.map((creator: IProfile) => ({
            id: creator.id,
            display: creator.username,
            name: creator.display_name,
            avatarUrl: creator.avatar.url || creator.cropped_avatar.url,
            friends: creator.friends_status === 'Friends',
            subscribed: creator.isSubscribed
        }))

        setLocalMentions(transformedResponse)

        return callback(transformedResponse)
    }

    const debouncedSearch = useCallback(
        debounce(
            async (term: string, callback) => await getCreators(term, callback),
            1000
        ),
        []
    )

    const handleChange = (
        event: { target: { value: string } },
        _newValue: string,
        _newPlainTextValue: string,
        mentions: Array<{
            id: string
            display: string
            plainTextIndex: number
        }>
    ) => {
        let newText = event.target.value

        if (newText.length > 150) {
            newText = newText.slice(0, 150)
        }

        const newTags = mentions.map(mention => ({
        user_id: mention.id,
        start: mention.plainTextIndex,
        end: mention.plainTextIndex + mention.display.length
        }))

        setText(newText, newTags)
    }

    return (
        <div
            ref={textWrapRef}
            className='newpost__body__textWrap'
            onClick={focusTextarea}
        >
            <MentionsInput
                className='newpost__body__text'
                style={{ suggestions: { top: '2rem' } }}
                value={body}
                maxLength={150}
                placeholder='Write Your Caption...'
                onChange={handleChange}
            >
                <Mention
                    trigger='@'
                    data={(search, callback) =>
                        debouncedSearch(search, callback)
                    }
                    appendSpaceOnAdd
                    markup='@{{__id__||__display__||__avatarUrl__}}'
                    displayTransform={(_id: any, display: any) =>
                        `@${display}`
                    }
                    renderSuggestion={(
                        entry,
                        _search,
                        _highlightedDisplay,
                        index
                    ) => {
                        const localMention = localMentions[index]

                        return (
                            <div className='newpost__body__text__mention'>
                                <div className='newpost__body__text__mention__left'>
                                    <div className='newpost__body__text__mention__avatar'>
                                        <img
                                            src={localMention?.avatarUrl}
                                            alt={t('avatar')}
                                        />
                                    </div>

                                    <div className='newpost__body__text__mention__content'>
                                        <div className='newpost__body__text__mention__content__name'>
                                            {localMention?.name}
                                        </div>

                                        <div className='newpost__body__text__mention__content__handle'>
                                            @{entry.display}
                                        </div>
                                    </div>
                                </div>

                                <div className='newpost__body__text__mention__right'>
                                    {localMention?.friends && (
                                        <span className='newpost__body__text__mention__status newpost__body__text__mention__status--friends'>
                                            {t('friends')}
                                        </span>
                                    )}

                                    {localMention?.subscribed && (
                                        <span className='newpost__body__text__mention__status newpost__body__text__mention__status--subscribed'>
                                            {t('subscribed')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )
                    }}
                />
            </MentionsInput>
        </div>
    )
}